import React, { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import DOMPurify from 'dompurify';
import { marked } from 'marked';

import { Box, type BoxProps } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import useAppNavigation from '~/hooks/useAppNavigation';
import { stripNavigableReportingApiLinks } from '~/utils/leData/stripReportingApiMarkdownLinks';

import {
  STREAM_FADE_CLASS,
  STREAM_FADE_MS,
  StreamingHtml,
  healStreamingTail,
  splitMarkdownBlocks,
} from './streamingMarkdown';

type Props = {
  content: string;
  preserveCodeFenceInfo?: boolean;
  boxProps?: BoxProps;
  streaming?: boolean;
};

function normalizeMarkdownContent(content: string): string {
  const normalized = content
    .replace(/\r\n/g, '\n')
    .replace(/^\n+/, '')
    .replace(/\t/g, '  ')
    .replace(/^[ \t]*[•◦▪]\s+/gm, '- ');

  const lines = normalized.split('\n');
  const nonEmptyLines = lines.filter((line) => line.trim().length > 0);

  if (nonEmptyLines.length === 0) {
    return normalized;
  }

  const indents = nonEmptyLines.map((line) => line.match(/^ +/)?.[0].length ?? 0).filter((indent) => indent > 0);
  if (indents.length === 0) {
    return normalized;
  }

  const minIndent = Math.min(...indents);
  if (minIndent <= 0) {
    return normalized;
  }

  return lines
    .map((line) => {
      if (line.trim().length === 0) {
        return line;
      }
      return line.startsWith(' '.repeat(minIndent)) ? line.slice(minIndent) : line;
    })
    .join('\n');
}

export function configureLinkTargets(html: string): string {
  if (!html.includes('<a')) {
    return html;
  }

  const template = document.createElement('template');
  template.innerHTML = html;

  template.content.querySelectorAll('a[href]').forEach((link) => {
    const href = link.getAttribute('href') ?? '';
    if (href.startsWith('/') || href.startsWith('#')) {
      link.removeAttribute('target');
      link.removeAttribute('rel');
    } else {
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noreferrer');
    }
  });

  return template.innerHTML;
}

/** Marker class carrying tabular-nums + weight (styling lives in baseSx, no colour). */
export const FIGURE_TOKEN_CLASS = 'le-figure-token';

/**
 * Text nodes inside these elements are never rewritten: code/pre are verbatim,
 * anchors must not gain figure spans (avoids reading as part of a link), and
 * strong/em/b/i are already emphasised runs.
 */
const FIGURE_SKIP_TAGS = new Set(['CODE', 'PRE', 'A', 'STRONG', 'EM', 'B', 'I', 'KBD', 'SAMP']);

const CURRENCY_CODES = 'AUD|USD|NZD|GBP|EUR|SGD|HKD|JPY|CAD|CHF|CNY';
// Integer part is either proper thousands grouping (1,234,567) or an ungrouped run of
// digits (42), never a malformed mix like 12,34; followed by an optional decimal fraction.
const NUMBER_CORE = '(?:\\d{1,3}(?:,\\d{3})+|\\d+)(?:\\.\\d+)?';
// Optional magnitude suffix (1.2M, 5m, 3bn, 4million). The trailing letter-boundary stops
// it bleeding into a following word: "AUD 5many" must not match "AUD 5m" and strand "any".
const MAGNITUDE = '(?:\\s?[KkMmBb](?:n|illion)?)?(?![A-Za-z])';
// A figure token must not start in the middle of another number, so "12,34%" does not
// match the trailing "34%" as a percentage.
const NOT_MID_NUMBER = '(?<![\\d.,])';

/**
 * Matches only clear figure tokens: currency ($1.2M, $23,400, AUD 5m), percentages
 * (12.3%), and large grouped numbers (1,234,567). Bare integers, years and version
 * strings do not match by design - they carry no currency symbol/code, percent sign
 * or thousands separator. Ordered alternation so the leftmost, most-specific form wins.
 */
const FIGURE_PATTERN = [
  `\\b(?:${CURRENCY_CODES})\\s?\\$?${NUMBER_CORE}${MAGNITUDE}`, // AUD 5m, USD $1,200
  `${NOT_MID_NUMBER}\\$${NUMBER_CORE}${MAGNITUDE}`, // $1.2M, $23,400
  `${NOT_MID_NUMBER}${NUMBER_CORE}%`, // 12.3%
  `${NOT_MID_NUMBER}\\d{1,3}(?:,\\d{3})+(?:\\.\\d+)?(?![\\d,])`, // 1,234,567
].join('|');

function createFigureRegex(): RegExp {
  return new RegExp(FIGURE_PATTERN, 'g');
}

function hasSkippedAncestor(node: Node): boolean {
  let element = node.parentElement;
  while (element) {
    if (FIGURE_SKIP_TAGS.has(element.tagName)) {
      return true;
    }
    element = element.parentElement;
  }
  return false;
}

function wrapFigureToken(text: string): DocumentFragment {
  const regex = createFigureRegex();
  const fragment = document.createDocumentFragment();
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    const [token] = match;
    if (token.length === 0) {
      regex.lastIndex += 1;
      continue;
    }
    if (match.index > lastIndex) {
      fragment.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
    }
    const span = document.createElement('span');
    span.className = FIGURE_TOKEN_CLASS;
    span.textContent = token;
    fragment.appendChild(span);
    lastIndex = match.index + token.length;
  }

  if (lastIndex < text.length) {
    fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
  }
  return fragment;
}

/**
 * Wraps standalone figure tokens in answer prose so numbers read as data (tabular-nums,
 * slightly heavier). Runs on the already-sanitized HTML and only ever sets textContent
 * on freshly-created spans, so it cannot reintroduce markup or an XSS vector. Skips
 * code/pre, links and already-emphasised runs, and leaves figure-free text untouched.
 */
function emphasizeFigures(html: string): string {
  if (!/\d/.test(html)) {
    return html;
  }

  const template = document.createElement('template');
  template.innerHTML = html;

  const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT);
  const detector = createFigureRegex();
  const targets: Text[] = [];

  for (let current = walker.nextNode(); current; current = walker.nextNode()) {
    const textNode = current as Text;
    const value = textNode.nodeValue;
    detector.lastIndex = 0;
    if (value && detector.test(value) && !hasSkippedAncestor(textNode)) {
      targets.push(textNode);
    }
  }

  targets.forEach((textNode) => {
    textNode.parentNode?.replaceChild(wrapFigureToken(textNode.nodeValue ?? ''), textNode);
  });

  return template.innerHTML;
}

/** Keep each answer table scrollable inside a narrow chat pane without splitting figures. */
function wrapAnswerTables(html: string): string {
  if (!html.includes('<table')) {
    return html;
  }
  const template = document.createElement('template');
  template.innerHTML = html;
  template.content.querySelectorAll('table').forEach((table) => {
    const frame = document.createElement('div');
    frame.className = 'le-answer-table-frame';
    const wrapper = document.createElement('div');
    wrapper.className = 'le-answer-table-scroll';
    table.replaceWith(frame);
    frame.appendChild(wrapper);
    wrapper.appendChild(table);
    const caption = document.createElement('p');
    caption.className = 'le-answer-table-hint';
    caption.textContent = 'Scroll to see all columns';
    frame.appendChild(caption);
    table.querySelectorAll('td').forEach((cell) => {
      const text = cell.textContent?.trim() ?? '';
      if (/^[+\-−]?(?:(?:[A-Z]{1,3})?[$€£]|[A-Z]{3}\s+)?\s*\d[\d,.%+\-−\s→/]*(?:pp|[KMB])?$/.test(text)) {
        cell.classList.add('le-answer-table-number');
      }
    });
  });
  return template.innerHTML;
}

function markdownToHtml(normalizedContent: string, preserveCodeFenceInfo: boolean): string {
  const raw = marked.parse(normalizedContent, {
    async: false,
    breaks: true,
    gfm: true,
    walkTokens(token) {
      if (preserveCodeFenceInfo && token.type === 'code' && token.lang) {
        token.text = `${token.lang}\n${token.text}`;
        token.lang = undefined;
      }
    },
  });
  const stripped = stripNavigableReportingApiLinks(raw);
  return wrapAnswerTables(emphasizeFigures(configureLinkTargets(DOMPurify.sanitize(stripped))));
}

const StreamingMarkdownBlock = memo(function StreamingMarkdownBlock({
  markdown,
  preserveCodeFenceInfo,
}: {
  markdown: string;
  preserveCodeFenceInfo: boolean;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [streamingHtml] = useState(() => new StreamingHtml());
  const html = useMemo(() => markdownToHtml(markdown, preserveCodeFenceInfo), [markdown, preserveCodeFenceInfo]);
  useLayoutEffect(() => {
    const target = ref.current;
    if (!target) {
      return undefined;
    }
    let timer = 0;
    const patch = () => {
      if (streamingHtml.patch(target, html, performance.now())) {
        timer = window.setTimeout(patch, STREAM_FADE_MS);
      }
    };
    patch();
    return () => window.clearTimeout(timer);
  }, [html, streamingHtml]);
  return <Box ref={ref} />;
});

function StreamingMarkdownBlocks({
  content,
  streaming,
  preserveCodeFenceInfo,
}: {
  content: string;
  streaming: boolean;
  preserveCodeFenceInfo: boolean;
}) {
  const blocks = useMemo(() => {
    const { blocks: all, definitions } = splitMarkdownBlocks(normalizeMarkdownContent(content));
    const shown = streaming && all.length > 0 ? [...all.slice(0, -1), healStreamingTail(all[all.length - 1])] : all;
    return definitions ? shown.map((block) => `${block}\n\n${definitions}`) : shown;
  }, [content, streaming]);
  return (
    <>
      {blocks.map((block, index) => (
        <StreamingMarkdownBlock key={index} markdown={block} preserveCodeFenceInfo={preserveCodeFenceInfo} />
      ))}
    </>
  );
}

/**
 * Like {@link MarkdownPreview}, but reporting API paths in links are not real anchors (avoids stray GETs).
 */
export default function LeDataMarkdownPreview({
  content,
  boxProps,
  preserveCodeFenceInfo = false,
  streaming = false,
}: Props) {
  const theme = useTheme();
  const { push } = useAppNavigation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [incremental] = useState(streaming);
  const html = useMemo(
    () => (incremental ? '' : markdownToHtml(normalizeMarkdownContent(content), preserveCodeFenceInfo)),
    [content, incremental, preserveCodeFenceInfo],
  );
  // Keep React from replacing unchanged markup and dropping observer-owned attributes.
  const innerHtml = useMemo(() => (incremental ? undefined : { __html: html }), [html, incremental]);

  const baseSx = useMemo(
    () => ({
      '& code': {
        // `grey[100]` is almost white in dark mode, making the light code
        // text disappear into an inverted chip. A quiet lifted surface keeps
        // inline code distinct without turning it into a bright box.
        backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.1) : 'rgba(0,0,0,0.1)',
        color: theme.palette.text.primary,
        padding: '0.2em 0.5em',
        borderRadius: '8px',
        fontSize: '0.875em',
        fontFamily: 'monospace',
        border: `1px solid ${theme.palette.divider}`,
      },
      '& pre': {
        backgroundColor:
          theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.06) : theme.palette.grey[100],
        color: theme.palette.text.primary,
        padding: 2,
        borderRadius: '8px',
        overflow: 'auto',
        '& code': {
          backgroundColor: 'transparent',
          padding: 0,
          border: 'none',
          borderRadius: 0,
        },
      },
      '& a': {
        color: theme.palette.primary.main,
      },
      [`& .${STREAM_FADE_CLASS}`]: {
        animation: `leStreamFade ${STREAM_FADE_MS}ms ease-out both`,
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      },
      '@keyframes leStreamFade': {
        from: { opacity: 0 },
        to: { opacity: 1 },
      },
      // Suisse Int'l declares 600 but not 700; render bold at 600 so it does not faux-bold.
      '& strong, & b': {
        fontWeight: 600,
        color: theme.palette.text.primary,
      },
      // Figure tokens emphasised in prose: tabular-nums + slightly heavier weight only,
      // deliberately no colour/background so they cannot be mistaken for links or chips.
      [`& .${FIGURE_TOKEN_CLASS}`]: {
        fontVariantNumeric: 'tabular-nums',
        fontWeight: 500,
      },
      '& blockquote': {
        borderLeft: `4px solid ${theme.palette.divider}`,
        margin: 0,
        paddingLeft: 2,
        color: theme.palette.text.secondary,
      },
      '& .le-answer-table-scroll': {
        maxWidth: '100%',
        overflowX: 'auto',
        my: 1,
      },
      '& .le-answer-table-hint': {
        display: 'none',
        color: theme.palette.text.secondary,
        fontSize: '0.75rem',
        mt: 0.5,
      },
      '& .le-answer-table-frame[data-overflow="true"] .le-answer-table-hint': {
        display: 'block',
      },
      '& table': {
        borderCollapse: 'collapse',
        minWidth: '100%',
        width: 'max-content',
        '& th, & td': {
          border: `1px solid ${theme.palette.divider}`,
          padding: 1,
          maxWidth: 280,
          overflowWrap: 'normal',
          wordBreak: 'normal',
        },
        '& .le-answer-table-number': {
          whiteSpace: 'nowrap',
          fontVariantNumeric: 'tabular-nums',
        },
        '& th': {
          backgroundColor: theme.palette.action.hover,
        },
      },
    }),
    [theme],
  );

  const { sx: boxSx, ...restBoxProps } = boxProps ?? {};
  useEffect(() => {
    const wrappers = containerRef.current?.querySelectorAll<HTMLElement>('.le-answer-table-scroll');
    if (!wrappers?.length) {
      return undefined;
    }
    const update = () =>
      wrappers.forEach((wrapper) => {
        wrapper.parentElement?.setAttribute('data-overflow', String(wrapper.scrollWidth > wrapper.clientWidth + 1));
      });
    const observer = new ResizeObserver(update);
    wrappers.forEach((wrapper) => {
      observer.observe(wrapper);
      const table = wrapper.querySelector('table');
      if (table) {
        observer.observe(table);
      }
    });
    update();
    return () => observer.disconnect();
  }, [content, html]);
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return undefined;
    }

    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) {
        return;
      }
      const link = target.closest<HTMLAnchorElement>('a[href]');
      const href = link?.getAttribute('href') ?? '';
      if (href.startsWith('/')) {
        event.preventDefault();
        const destination = new URL(href, window.location.href);
        if (destination.pathname === window.location.pathname) {
          // The unified workspace is one route with several URL-backed views. Its own
          // controls write the address directly; mirror that contract here, then ask
          // the shell to restore the linked Lens without a full-page reload.
          window.history.pushState(
            window.history.state,
            '',
            `${destination.pathname}${destination.search}${destination.hash}`,
          );
          window.dispatchEvent(new PopStateEvent('popstate', { state: window.history.state }));
        } else {
          push(href);
        }
      }
    };

    container.addEventListener('click', handleClick, true);
    return () => container.removeEventListener('click', handleClick, true);
  }, [push]);

  return (
    <Box
      ref={containerRef}
      dangerouslySetInnerHTML={innerHtml}
      sx={[
        { maxWidth: '100%', minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' },
        baseSx,
        ...(Array.isArray(boxSx) ? boxSx : boxSx ? [boxSx] : []),
      ]}
      {...restBoxProps}
    >
      {incremental ? (
        <StreamingMarkdownBlocks
          content={content}
          streaming={streaming}
          preserveCodeFenceInfo={preserveCodeFenceInfo}
        />
      ) : undefined}
    </Box>
  );
}
